/**
 * 将 API 返回的 ISO 时间（含 +08:00 或 Z）统一格式化为北京时间展示串。
 * 使用 Intl + Asia/Shanghai，避免依赖浏览器本地时区。
 */

const NAIVE_ISO_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;

/**
 * 统一解析后端时间：
 * - Date / number: 直接解析
 * - ISO 且无时区: 视为 UTC（兼容历史 timestamp without time zone 的返回）
 * - ISO 含 Z / ±hh:mm: 按标准解析
 */
export function parseApiDateTime(value) {
  if (!value && value !== 0) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const text = String(value).trim();
  if (!text) return null;
  const normalized = NAIVE_ISO_RE.test(text) ? `${text}Z` : text;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

export function getApiTimestampMs(value) {
  const d = parseApiDateTime(value);
  return d ? d.getTime() : 0;
}

/**
 * 纠正本地缓存的 lastSeen 时间戳。
 * 历史上若出现时区展示错误，可能写入“未来时间”，导致新通知一直不计未读。
 */
export function normalizeLastSeenMs(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return 0;
  const now = Date.now();
  // 允许少量时钟偏差，超过 5 分钟视为异常并重置。
  if (num > now + 5 * 60 * 1000) return 0;
  return num;
}

function _parts(isoString, options) {
  const d = parseApiDateTime(isoString);
  if (!d) return null;
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: 'Asia/Shanghai',
    ...options,
  }).formatToParts(d);
}

const _get = (parts, t) => (parts || []).find((p) => p.type === t)?.value ?? '';

/**
 * @param {string | null | undefined} isoString
 * @returns {string}
 */
export function formatBeijingDateTime(isoString) {
  const parts = _parts(isoString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  if (!parts) return '-';
  return `${_get(parts, 'year')}-${_get(parts, 'month')}-${_get(parts, 'day')} ${_get(parts, 'hour')}:${_get(parts, 'minute')}:${_get(parts, 'second')}`;
}

/**
 * @param {string | null | undefined} isoString
 * @returns {string}
 */
export function formatBeijingDateOnly(isoString) {
  const parts = _parts(isoString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  if (!parts) return '-';
  return `${_get(parts, 'year')}-${_get(parts, 'month')}-${_get(parts, 'day')}`;
}
